import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

// user states:
//   undefined -> initial checking
//   null      -> not signed in
//   {...}     -> signed in
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const u = await api.authMe();
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const u = await api.authLogin(email, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.authLogout();
    } catch (e) {
      // Cookie-cleared logout: server-side failure shouldn't strand the
      // client in a signed-in state, but do log so we don't hide real bugs.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[AuthContext] Server logout call failed:", e);
      }
    }
    setUser(null);
  }, []);

  const value = { user, login, logout, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
